import { createListCollection } from "@ark-ui/react";
import { useMemo } from "react";
import classnames from "classnames";

import mapControlOverlayStyles from "@/components/map/map-control-overlay.module.css";
import MapControlLegend from "@/components/map/map-control-legend";
import MapControlTabs from "@/components/map/map-control-tabs";
import { useMapControlLegendState } from "@/components/map/use-map-control-legend-state";
import Icon from "@/components/icon/icon";
import Select from "@/components/select/select";
import { MapLayersController } from "@/lib/map-layers";
import { useTranslation } from "@/lib/i18n";
import EnumControl from "@/features/shared/widgets/map/controls/enum-control";

import layerControlStyles from "./model-map-layer-control.module.css";
import {
  ModelMapStaticOverlaysPanel,
  type StaticOverlayUiById,
} from "./model-map-static-overlays-panel";
import type { ModelMapStaticOverlayEntry } from "./use-model-map-static-overlays";

interface ModelMapLayerControlProps {
  mapLayers: MapLayersController;
  selectedLayerId: string | undefined;
  updateSelectedLayer: (layerId: string) => void;
  staticOverlayEntries: ModelMapStaticOverlayEntry[];
  staticOverlayUiById: StaticOverlayUiById;
  onSetStaticOverlayVisible: (instanceId: string, visible: boolean) => void;
}

const ModelMapLayerControl = ({
  mapLayers,
  selectedLayerId,
  updateSelectedLayer,
  staticOverlayEntries,
  staticOverlayUiById,
  onSetStaticOverlayVisible,
}: ModelMapLayerControlProps) => {
  const { t } = useTranslation();
  const {
    activePanel,
    setActivePanel,
    selectedLegendLayerId,
    setSelectedLegendLayerId,
    hasLayersWithLegend,
  } = useMapControlLegendState(mapLayers.layers);

  const layerCollection = createListCollection({
    items: mapLayers.layers.map((layer) => ({
      label: layer.title,
      value: layer.id,
    })),
  });

  const hasGriddedLayers = mapLayers.layers.length > 0;

  const griddedLayerSelectValue = useMemo(() => {
    if (!mapLayers.layers.length) return undefined;
    if (selectedLayerId && mapLayers.layers.some((l) => l.id === selectedLayerId)) {
      return selectedLayerId;
    }
    return mapLayers.layers[0]?.id;
  }, [mapLayers.layers, selectedLayerId]);

  return (
    <MapControlTabs.Root value={activePanel} onValueChange={setActivePanel}>
      <MapControlTabs.List>
        <MapControlTabs.Trigger value={"config"} aria-label={t("map.config_tab_label")}>
          <Icon name="layers" size={20} />
        </MapControlTabs.Trigger>
        {hasLayersWithLegend && (
          <MapControlTabs.Trigger value={"legend"} aria-label={t("map.legend_tab_label")}>
            <Icon name="info" size={20} />
          </MapControlTabs.Trigger>
        )}
      </MapControlTabs.List>

      <MapControlTabs.Content value={"config"}>
        <div className={layerControlStyles.configColumn}>
          {/* controlOverlay uses vertical padding; zero it here so min-height matches one static row. */}
          <div
            className={classnames(
              mapControlOverlayStyles.controlOverlay,
              layerControlStyles.griddedFrostedPanel,
            )}
            style={{ paddingTop: 0, paddingBottom: 0 }}
          >
            {hasGriddedLayers && griddedLayerSelectValue ? (
              <Select
                collection={layerCollection}
                value={griddedLayerSelectValue}
                onValueChange={updateSelectedLayer}
                positioning={{ offset: { mainAxis: 8, crossAxis: 0 } }}
                variant="ghost"
                onlyLightTheme
              />
            ) : (
              <span className={layerControlStyles.emptyState}>
                {t("map.model_map_no_gridded_layers")}
              </span>
            )}
          </div>
          <ModelMapStaticOverlaysPanel
            entries={staticOverlayEntries}
            uiById={staticOverlayUiById}
            onSetVisible={onSetStaticOverlayVisible}
          />
          {/* Last in the column: dimension pickers open downwards, so keep the panels
              that can be toggled (static overlays) above them. */}
          <EnumControl {...mapLayers} className={layerControlStyles.enumPanel} />
        </div>
      </MapControlTabs.Content>
      {hasLayersWithLegend && (
        <MapControlTabs.Content value={"legend"}>
          <MapControlLegend
            layers={mapLayers.layers}
            selectedLegendLayerId={selectedLegendLayerId}
            onSelectedLegendLayerIdChange={setSelectedLegendLayerId}
          />
        </MapControlTabs.Content>
      )}
    </MapControlTabs.Root>
  );
};

export default ModelMapLayerControl;
